// src/components/AssignApplicantPopup.js
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaTimes, FaUserPlus, FaSpinner, FaExclamationTriangle, FaUserCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import apiClient from '../api/apiClient'; // Assuming apiClient is configured

const AssignApplicantPopup = ({ isOpen, onClose, orderItemId }) => {
    // --- State ---
    const [isLoading, setIsLoading] = useState(false); // Overall loading (status check, applicants fetch)
    const [error, setError] = useState(null); // General API error messages
    const [applicants, setApplicants] = useState([]); // List of user's existing applicants
    const [selectedApplicantId, setSelectedApplicantId] = useState(''); // ID of selected existing applicant
    const [showCreateForm, setShowCreateForm] = useState(false); // Toggle for new applicant form
    const [newApplicantName, setNewApplicantName] = useState(''); // Name for new applicant input
    const [isCreating, setIsCreating] = useState(false); // Loading state for creating applicant
    const [isAssigning, setIsAssigning] = useState(false); // Loading state for assigning applicant
    const [assignmentChecked, setAssignmentChecked] = useState(false); // Track if initial check was done

    // --- Helper Functions ---
    const resetState = useCallback(() => {
        setIsLoading(false);
        setError(null);
        setApplicants([]);
        setSelectedApplicantId('');
        setShowCreateForm(false);
        setNewApplicantName('');
        setIsCreating(false);
        setIsAssigning(false);
        setAssignmentChecked(false);
    }, []);

    // --- Effects ---
    // Fetch initial status and applicants when popup opens
    useEffect(() => {
        if (!isOpen || !orderItemId) {
            resetState();
            return;
        }

        const checkStatusAndFetchApplicants = async () => {
            setIsLoading(true);
            setError(null);
            setAssignmentChecked(false);
            console.log(`[AssignPopup] Checking assignment status for Item ID: ${orderItemId}`);

            try {
                // 1. Check if already assigned
                const statusResponse = await apiClient.get(`/order-items/${orderItemId}/assignment-status`);
                setAssignmentChecked(true); // Mark check as done

                if (statusResponse.data?.isAssigned) {
                    console.log(`[AssignPopup] Item ${orderItemId} already assigned to ${statusResponse.data.applicantName} (ID: ${statusResponse.data.applicantId}). Closing popup.`);
                    toast.info(`Item already assigned to ${statusResponse.data.applicantName || 'an applicant'}.`);
                    onClose(true); // Signal success (assignment exists)
                    setIsLoading(false);
                    return; // Don't proceed further
                }

                // 2. If not assigned, fetch existing applicants
                console.log(`[AssignPopup] Item ${orderItemId} not assigned. Fetching user's applicants.`);
                const applicantsResponse = await apiClient.get('/applicants');
                setApplicants(applicantsResponse.data || []);
                setIsLoading(false);

            } catch (err) {
                console.error("[AssignPopup] Error fetching status or applicants:", err);
                const errorMsg = err.response?.data?.error || err.message || "Could not load assignment details or applicants.";
                setError(errorMsg);
                setIsLoading(false);
            }
        };

        checkStatusAndFetchApplicants();

        // Cleanup function
        return () => {
            // Optional: Reset state on close if desired, handled by isOpen check mainly
            // resetState();
        };

    }, [isOpen, orderItemId, onClose, resetState]); // Dependencies

    // --- Event Handlers ---
    const handleSelectApplicantChange = (e) => {
        setSelectedApplicantId(e.target.value);
        setShowCreateForm(false); // Hide create form if existing selected
        setError(null); // Clear error on selection change
    };

    const handleToggleCreateForm = (e) => {
        e.preventDefault(); // Prevent potential form submission
        setShowCreateForm(prev => !prev);
        setSelectedApplicantId(''); // Deselect existing if opening create form
        setNewApplicantName(''); // Clear name field
        setError(null); // Clear error
    };

    const handleNewApplicantNameChange = (e) => {
        setNewApplicantName(e.target.value);
    };

    // Core Assign Logic (called by other handlers)
    const handleAssignApplicant = async (applicantIdToAssign) => {
        if (!applicantIdToAssign || !orderItemId) {
            setError("Missing applicant or order item ID for assignment.");
            return;
        }

        setIsAssigning(true);
        setError(null);
        console.log(`[AssignPopup] Attempting to assign Applicant ${applicantIdToAssign} to Item ${orderItemId}`);

        try {
            const response = await apiClient.post(`/order-items/${orderItemId}/assign-applicant`, {
                applicantId: applicantIdToAssign
            });

            // Ensure assignment API returns a success indicator
            if (response.data?.status === 'success' || response.status === 200 || response.status === 204) { // Adjust based on actual assign API response
                toast.success(response.data?.message || "Applicant assigned successfully!");
                onClose(true); // Signal success and close
            } else {
                // Handle cases where backend returns success=false or unexpected status
                throw new Error(response.data?.message || `Assignment failed. Please try again.`);
            }
        } catch (err) {
            console.error("[AssignPopup] Error assigning applicant:", err);
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to assign applicant.";
            setError(errorMsg);
            toast.error(`Assignment failed: ${errorMsg}`);
        } finally {
            setIsAssigning(false);
        }
    };

    // Handler for the "Create & Assign" button *** UPDATED SECTION ***
    const handleCreateAndAssign = async (e) => {
        e.preventDefault();
        if (!newApplicantName.trim()) {
            setError("Please enter a name for the new applicant.");
            return;
        }

        setIsCreating(true);
        setError(null);
        console.log(`[AssignPopup] Creating new applicant: ${newApplicantName.trim()}`);

        try {
            // Make the API call to create the applicant
            const createResponse = await apiClient.post('/applicants', {
                full_name: newApplicantName.trim(),
                // Add relationship_to_user if you want to collect it here
            });

            // Log the actual response for easier debugging in the future
            console.log(">>> API Response Data for POST /applicants:", createResponse.data);

            // Access the ID and name directly from the response data object
            const applicantId = createResponse.data?.id;
            const applicantName = createResponse.data?.full_name;

            // Check if the directly accessed ID exists
            if (applicantId) {
                toast.success(`Applicant "${applicantName || 'New Applicant'}" created.`);
                // Now assign the newly created applicant using the retrieved ID
                // This will set isAssigning=true internally
                await handleAssignApplicant(applicantId); // Pass the correct ID
            } else {
                 // Handle case where API returns 201 but the ID is missing in the response body
                 console.error("Applicant creation successful (201), but ID missing in response body:", createResponse.data);
                 throw new Error("Applicant created, but the server's response did not include the expected ID.");
            }

        } catch (err) {
             console.error("[AssignPopup] Error creating or assigning applicant:", err); // Changed log slightly
             let errorMsg = err.response?.data?.error || err.message || "Failed to create applicant.";

             // Handle 409 Conflict specifically (Applicant already exists)
             // Check if the response for 409 *also* includes the applicant data directly
             if (err.response?.status === 409 && err.response?.data?.id) {
                 const existingApplicant = err.response.data; // Assuming 409 response is similar to 201 response
                 errorMsg = `Applicant "${existingApplicant.full_name}" already exists. Selecting existing applicant.`;
                 setError(errorMsg);
                 setSelectedApplicantId(String(existingApplicant.id)); // Auto-select existing
                 setShowCreateForm(false); // Hide create form
                 toast.warn(errorMsg);
             } else if (err.response?.status === 409 && err.response?.data?.applicant?.id) {
                // Fallback if the 409 response *does* nest it under 'applicant'
                 const existingApplicant = err.response.data.applicant;
                 errorMsg = `Applicant "${existingApplicant.full_name}" already exists. Selecting existing applicant.`;
                 setError(errorMsg);
                 setSelectedApplicantId(String(existingApplicant.id));
                 setShowCreateForm(false);
                 toast.warn(errorMsg);
             } else {
                 // General error during creation
                setError(errorMsg);
                toast.error(`Creation failed: ${errorMsg}`);
             }
        } finally {
            // isAssigning state is handled within handleAssignApplicant
            setIsCreating(false);
        }
    };
    // *** END OF UPDATED SECTION ***

    // Handler for the "Assign Selected" button
    const handleAssignExisting = (e) => {
        e.preventDefault();
        if (!selectedApplicantId) {
            setError("Please select an existing applicant to assign.");
            return;
        }
        // Ensure ID is passed correctly (handleAssignApplicant expects a number or string ID)
        handleAssignApplicant(selectedApplicantId);
    };

    const handleModalClick = (e) => e.stopPropagation(); // Prevent overlay click closing modal

    // --- Render Logic ---
    if (!isOpen) return null;

    // Combined loading/disabling state
    const canInteract = !isLoading && !isCreating && !isAssigning;

    return (
        <div style={styles.overlay} onClick={() => canInteract && onClose(false)} role="dialog" aria-modal="true" aria-labelledby="assign-popup-title">
            <div style={styles.modalContent} onClick={handleModalClick}>
                {/* Header */}
                <button
                    style={{...styles.closeButton, opacity: canInteract ? 1 : 0.5}}
                    onClick={() => canInteract && onClose(false)}
                    aria-label="Close Popup"
                    disabled={!canInteract}
                >
                    <FaTimes />
                </button>
                <h2 id="assign-popup-title" style={styles.title}>
                    <FaUserCheck style={{ marginRight: '12px', color: '#17a2b8' }} /> Assign Applicant
                </h2>
                <p style={styles.subtitle}>Select or create an applicant for Order Item ID: <strong>{orderItemId}</strong></p>

                {/* Loading State (Initial Load) */}
                {isLoading && (
                    <div style={styles.centeredMessage}>
                        <FaSpinner className="fa-spin fa-lg" style={{ marginRight: '10px' }} /> Loading assignment details...
                    </div>
                )}

                {/* Error Display */}
                {error && !isLoading && ( // Don't show error during initial load
                     <div style={styles.errorDisplay}>
                         <FaExclamationTriangle style={{ marginRight: '8px' }}/> {error}
                     </div>
                )}

                {/* Main Content Area (only show if initial checks done and no initial loading/error) */}
                {!isLoading && !error && assignmentChecked && (
                    <div style={styles.contentArea}>
                         {/* Existing Applicants Section */}
                         {applicants.length > 0 && (
                             <fieldset style={styles.fieldset} disabled={!canInteract}>
                                 <legend style={styles.legend}>Select Existing Applicant</legend>
                                 <div style={styles.radioGroup}>
                                     {applicants.map((applicant) => (
                                         <div key={applicant.id} style={styles.radioItem}>
                                             <input
                                                 type="radio"
                                                 id={`applicant-${applicant.id}`}
                                                 name="selectedApplicant"
                                                 value={String(applicant.id)} // Use string value for comparison
                                                 checked={selectedApplicantId === String(applicant.id)}
                                                 onChange={handleSelectApplicantChange}
                                                 disabled={!canInteract}
                                                 style={{ marginRight: '8px' }}
                                             />
                                             <label htmlFor={`applicant-${applicant.id}`} style={styles.radioLabel}>
                                                 {applicant.full_name}
                                             </label>
                                         </div>
                                     ))}
                                 </div>
                                 <button
                                      type="button"
                                      style={{ ...styles.button, ...styles.assignButton, marginTop: '10px', opacity: (!selectedApplicantId || !canInteract) ? 0.6 : 1 }}
                                      onClick={handleAssignExisting}
                                      disabled={!selectedApplicantId || !canInteract || isAssigning} // Disable while assigning
                                    >
                                      {isAssigning ? <><FaSpinner className="fa-spin" style={{marginRight: '8px'}}/> Assigning...</> : 'Assign Selected'}
                                  </button>
                             </fieldset>
                         )}
                         {applicants.length === 0 && !showCreateForm && (
                             <p style={styles.centeredMessage}>No existing applicants found for your account.</p>
                         )}

                         {/* Divider or Button to show create form */}
                         <div style={styles.divider}>
                             <span style={styles.dividerText}>OR</span>
                         </div>
                         <button
                              type="button"
                              style={{ ...styles.button, ...styles.toggleButton, width: '100%', marginBottom: '15px' }}
                              onClick={handleToggleCreateForm}
                              disabled={!canInteract} // Only disable based on overall interaction state
                            >
                              <FaUserPlus style={{ marginRight: '8px' }}/> {showCreateForm ? 'Cancel Create New' : 'Create New Applicant'}
                          </button>

                         {/* Create New Applicant Form */}
                         {showCreateForm && (
                             <form onSubmit={handleCreateAndAssign}>
                                 <fieldset style={styles.fieldset} disabled={!canInteract}> {/* Fieldset disabled based on overall interaction */}
                                     <legend style={styles.legend}>Create New Applicant</legend>
                                     <label htmlFor="newApplicantName" style={styles.inputLabel}>Full Name:</label>
                                     <input
                                         type="text"
                                         id="newApplicantName"
                                         value={newApplicantName}
                                         onChange={handleNewApplicantNameChange}
                                         style={styles.inputField}
                                         placeholder="Enter applicant's full name"
                                         required
                                         disabled={!canInteract} // Input disabled based on overall interaction
                                     />
                                     {/* Add more fields like relationship if needed */}
                                     <button
                                         type="submit"
                                         style={{ ...styles.button, ...styles.createButton, marginTop: '10px', opacity: (!newApplicantName.trim() || !canInteract) ? 0.6 : 1 }}
                                         disabled={!newApplicantName.trim() || !canInteract} // Button disabled if no name or cannot interact
                                        >
                                          {isCreating ? <><FaSpinner className="fa-spin" style={{marginRight: '8px'}}/> Creating...</> : (isAssigning ? <><FaSpinner className="fa-spin" style={{marginRight: '8px'}}/> Assigning...</> : 'Create & Assign')}
                                     </button>
                                 </fieldset>
                             </form>
                         )}
                     </div>
                )}

                {/* Footer */}
                <div style={styles.footer}>
                     <button
                        type="button"
                        style={{ ...styles.button, ...styles.cancelButton, opacity: canInteract ? 1 : 0.5 }}
                        onClick={() => canInteract && onClose(false)}
                        disabled={!canInteract}
                    >
                        Cancel
                     </button>
                     {/* Assign Selected Button is inside the existing applicants section */}
                </div>
            </div>
        </div>
    );
};

AssignApplicantPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired, // Should accept boolean: onClose(didAssignSuccessfully)
    orderItemId: PropTypes.number, // Can be null/undefined initially
};

// --- Styles --- (Styles remain unchanged)
const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' },
    modalContent: { background: 'white', padding: '25px 30px', borderRadius: '10px', boxShadow: '0 8px 25px rgba(0,0,0,0.15)', width: '95%', maxWidth: '550px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', position: 'relative' },
    closeButton: { position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#888', lineHeight: 1, padding: '5px', transition: 'color 0.2s ease, opacity 0.2s ease', '&:hover': { color: '#333' }, '&:disabled': { cursor: 'not-allowed'} },
    title: { marginTop: 0, marginBottom: '5px', color: '#333', fontSize: '1.5em', fontWeight: 600, display: 'flex', alignItems: 'center', marginRight: '40px' },
    subtitle: { marginTop: 0, marginBottom: '20px', color: '#555', fontSize: '0.95em', borderBottom: '1px solid #eee', paddingBottom: '15px' },
    contentArea: { flexGrow: 1, overflowY: 'auto', paddingRight: '10px', marginRight: '-10px', maxHeight: 'calc(90vh - 220px)' }, // Adjust max height if needed
    fieldset: { border: '1px solid #ddd', borderRadius: '6px', padding: '15px', marginBottom: '20px' },
    legend: { fontWeight: '600', padding: '0 8px', color: '#495057', fontSize: '1.1em' },
    radioGroup: { /* Container for radio buttons */ },
    radioItem: { marginBottom: '10px', display: 'flex', alignItems: 'center' },
    radioLabel: { marginLeft: '8px', fontWeight: 500, color: '#333', cursor: 'pointer' },
    inputLabel: { display: 'block', marginBottom: '6px', fontWeight: '500', color: '#495057' },
    inputField: { width: '100%', padding: '10px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '1em', boxSizing: 'border-box' },
    divider: { textAlign: 'center', margin: '20px 0', borderTop: '1px solid #eee', position: 'relative' },
    dividerText: { position: 'absolute', top: '-0.7em', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '0 10px', color: '#888', fontWeight: '500' },
    footer: { borderTop: '1px solid #ddd', paddingTop: '20px', marginTop: '20px', display: 'flex', justifyContent: 'flex-end' },
    button: { padding: '10px 20px', borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: '500', fontSize: '1em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', lineHeight: 1.5, '&:disabled': { cursor: 'not-allowed', opacity: 0.6 } },
    assignButton: { backgroundColor: '#007bff', color: 'white', '&:hover:not(:disabled)': { backgroundColor: '#0056b3' } },
    createButton: { backgroundColor: '#28a745', color: 'white', '&:hover:not(:disabled)': { backgroundColor: '#218838' } },
    toggleButton: { backgroundColor: '#f8f9fa', color: '#495057', border: '1px solid #ced4da', '&:hover:not(:disabled)': { backgroundColor: '#e2e6ea' } },
    cancelButton: { backgroundColor: '#6c757d', color: 'white', marginRight: '10px', '&:hover:not(:disabled)': { backgroundColor: '#5a6268' } },
    centeredMessage: { textAlign: 'center', padding: '20px', color: '#6c757d', fontStyle: 'italic', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    errorDisplay: { backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', padding: '12px 15px', borderRadius: '6px', marginBottom: '15px', fontSize: '0.95em', display: 'flex', alignItems: 'center', gap: '10px' },
};

export default AssignApplicantPopup;